import DOMPurify from 'dompurify'
import type { UponSanitizeAttributeHookEvent, UponSanitizeElementHookEvent } from 'dompurify'

const EMAIL_ALLOWED_TAGS = [
  'a',
  'p',
  'br',
  'div',
  'span',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'blockquote',
  'pre',
  'code',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'hr',
  'img',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'style',
]

const EMAIL_ALLOWED_ATTR = [
  'href',
  'src',
  'alt',
  'title',
  'class',
  'id',
  'width',
  'height',
  'align',
  'valign',
  'bgcolor',
  'cellpadding',
  'cellspacing',
  'border',
  'colspan',
  'rowspan',
  'style',
]

/**
 * Presentational table/image attributes that carry no URI and must therefore
 * bypass ALLOWED_URI_REGEXP. DOMPurify runs every attribute value that isn't in
 * URI_SAFE_ATTRIBUTES through that regexp (purify.es.mjs `_isValidAttribute`),
 * so the strict http/mailto/tel/cid pattern below silently dropped `width="100%"`,
 * `align="center"`, `cellpadding="0"` and friends even though EMAIL_ALLOWED_ATTR
 * lists them. Email HTML is table-layout HTML: without those attributes every
 * table collapses to shrink-to-fit and the rendered message loses its layout
 * (full-bleed footers and headers render as narrow content-width strips).
 * Only attributes that can never reference a resource or a navigation target
 * belong here — `href` and `src` stay gated by the regexp AND by the explicit
 * per-attribute checks in `onSanitizeAttribute`.
 */
const EMAIL_URI_SAFE_ATTR = [
  'width',
  'height',
  'align',
  'valign',
  'bgcolor',
  'cellpadding',
  'cellspacing',
  'border',
  'colspan',
  'rowspan',
]

const EMAIL_FORBID_TAGS = [
  'script',
  'iframe',
  'object',
  'embed',
  'form',
  'input',
  'button',
  'textarea',
  'select',
]

const EMAIL_SAFE_URI_PATTERN = /^(?:https?:\/\/[^\s<>"']+|mailto:[^\s<>"']+|tel:[0-9+().\-\s]+|cid:[^\s<>"']+)$/i
const EMAIL_SAFE_HREF_PATTERN = /^(?:https?:\/\/[^\s<>"']+|mailto:[^\s<>"']+|tel:[0-9+().\-\s]+)$/i
const EMAIL_SAFE_SRC_PATTERN = /^(?:https?:\/\/[^\s<>"']+|cid:[^\s<>"']+)$/i

/**
 * CSS declaration allowlist. Deliberately excludes every property that
 * conventionally carries a resource reference or behavioral hook
 * (background-image, cursor, content, mask*, filter, clip-path, position,
 * top/left/right/bottom/z-index, src, ...) so an "expected" property name can
 * never smuggle a url()/expression() payload past this list. CSS_DANGEROUS_PATTERN
 * below is a second, independent layer that denies those constructs by value
 * regardless of property.
 */
const CSS_ALLOWED_PROPERTIES = new Set([
  'color',
  'background',
  'background-color',
  'font',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-variant',
  'font-stretch',
  'line-height',
  'letter-spacing',
  'word-spacing',
  'text-align',
  'text-decoration',
  'text-decoration-line',
  'text-decoration-color',
  'text-decoration-style',
  'text-indent',
  'text-transform',
  'text-overflow',
  'white-space',
  'word-break',
  'word-wrap',
  'overflow-wrap',
  'vertical-align',
  'direction',
  'width',
  'height',
  'min-width',
  'min-height',
  'max-width',
  'max-height',
  'margin',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-width',
  'border-style',
  'border-color',
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
  'border-top-style',
  'border-right-style',
  'border-bottom-style',
  'border-left-style',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'border-radius',
  'border-collapse',
  'border-spacing',
  'table-layout',
  'empty-cells',
  'caption-side',
  'display',
  'float',
  'clear',
  'overflow',
  'overflow-x',
  'overflow-y',
  'visibility',
  'list-style',
  'list-style-type',
  'list-style-position',
  'box-sizing',
  'opacity',
])

/**
 * Matched against CSS text *after* CSS escape sequences have been resolved
 * (see unescapeCssTokens), so a backslash-hex payload like `\75 rl(` cannot
 * hide `url(` from this check the way it defeats a plain string denylist
 * applied to raw text. A bare backslash surviving unescaping means either a
 * malformed escape or something adversarial, so it's denied too.
 */
const CSS_DANGEROUS_PATTERN = /url\s*\(|@import|expression\s*\(|-moz-binding|behavior\s*:|javascript:|vbscript:|<\/style|\\/i

/**
 * Resolves CSS Syntax Level 3 escape sequences (`\` + 1-6 hex digits, or `\`
 * + any other single character) to the literal character/code point they
 * represent — https://www.w3.org/TR/css-syntax-3/#consume-escaped-code-point.
 * This is what defeats hex-escape bypasses of CSS_DANGEROUS_PATTERN: we test
 * the resolved text, not the raw text a browser's CSS tokenizer would see.
 */
function unescapeCssTokens(value: string): string {
  return value.replace(
    /\\([0-9a-fA-F]{1,6})[ \t\n\r\f]?|\\([\s\S])/g,
    (_match: string, hex?: string, literal?: string) => {
      if (hex) {
        const codePoint = Number.parseInt(hex, 16)
        if (codePoint === 0 || codePoint > 0x10ffff || (codePoint >= 0xd800 && codePoint <= 0xdfff)) {
          return '�'
        }
        return String.fromCodePoint(codePoint)
      }
      return literal ?? ''
    },
  )
}

function sanitizeCssValue(rawValue: string): string {
  const unescaped = unescapeCssTokens(rawValue).trim()
  if (!unescaped || CSS_DANGEROUS_PATTERN.test(unescaped)) return ''
  return unescaped
}

/** Quote-aware split on top-level `;` so a value containing `;` inside a quoted string isn't cut in half. */
function splitCssDeclarations(block: string): string[] {
  const parts: string[] = []
  let current = ''
  let quote: '"' | "'" | null = null
  for (const char of block) {
    if (quote) {
      current += char
      if (char === quote) quote = null
      continue
    }
    if (char === '"' || char === "'") {
      quote = char
      current += char
      continue
    }
    if (char === ';') {
      parts.push(current)
      current = ''
      continue
    }
    current += char
  }
  if (current.trim()) parts.push(current)
  return parts
}

/** Sanitizes a flat `prop: value; prop2: value2` declaration list — an inline `style` attribute, or one CSS rule's body. */
function sanitizeCssDeclarationBlock(block: string): string {
  const kept: string[] = []
  for (const rawDeclaration of splitCssDeclarations(block)) {
    const colonIndex = rawDeclaration.indexOf(':')
    if (colonIndex === -1) continue
    const property = unescapeCssTokens(rawDeclaration.slice(0, colonIndex)).trim().toLowerCase()
    if (!CSS_ALLOWED_PROPERTIES.has(property)) continue
    const value = sanitizeCssValue(rawDeclaration.slice(colonIndex + 1))
    if (!value) continue
    kept.push(`${property}: ${value}`)
  }
  return kept.join('; ')
}

/**
 * Sanitizes the text content of a `<style>` block. This is a bounded,
 * non-spec-complete CSS parser: it only understands flat `selector { decls }`
 * rulesets. At-rules (`@media`, `@import`, `@font-face`, `@keyframes`, ...)
 * need nested-block handling this parser doesn't attempt, so any `@` drops
 * the whole block rather than risk misreading nested braces into malformed
 * "safe-looking" output. A full CSS-spec parser was judged out of scope for
 * this fix — see email-html-sanitize.test.ts for the bypass classes this
 * closes instead.
 */
function sanitizeCssStyleBlock(cssText: string): string {
  const withoutComments = cssText.replace(/\/\*[\s\S]*?\*\//g, '')
  if (withoutComments.includes('@')) return ''

  const rules: string[] = []
  let index = 0
  while (index < withoutComments.length) {
    const openBrace = withoutComments.indexOf('{', index)
    if (openBrace === -1) break
    const closeBrace = withoutComments.indexOf('}', openBrace)
    if (closeBrace === -1) break

    const selector = unescapeCssTokens(withoutComments.slice(index, openBrace)).trim()
    const declarations = sanitizeCssDeclarationBlock(withoutComments.slice(openBrace + 1, closeBrace))
    index = closeBrace + 1

    if (!selector || CSS_DANGEROUS_PATTERN.test(selector) || !declarations) continue
    rules.push(`${selector} { ${declarations}; }`)
  }
  return rules.join('\n')
}

let emailPurifier: ReturnType<typeof DOMPurify> | null = null

/**
 * A DOMPurify instance scoped to this module (via the documented `DOMPurify(window)`
 * factory). Hooks are added/removed around every sanitize() call below so they never
 * leak into the other, unrelated DOMPurify.sanitize() call sites in the app that share
 * the default singleton import.
 */
function getEmailPurifier() {
  if (!emailPurifier) emailPurifier = DOMPurify(window)
  return emailPurifier
}

export function sanitizeEmailHtml(html: string): string {
  if (!html) return ''
  if (typeof window === 'undefined') return ''

  const purifier = getEmailPurifier()

  // Extracting <style> content here — rather than pre-parsing the raw string with our
  // own `.innerHTML =` round trip before DOMPurify ever sees it — is what keeps
  // DOMPurify's own mXSS hardening in the loop: it sees (and walks) the original
  // string exactly once, in its own sanitizing parse pass.
  const onSanitizeElement = (node: Node, data: UponSanitizeElementHookEvent) => {
    if (data.tagName !== 'style') return
    const sanitizedCss = sanitizeCssStyleBlock(node.textContent ?? '')
    if (!sanitizedCss) {
      ;(node as Element).remove()
      return
    }
    node.textContent = sanitizedCss
  }

  const onSanitizeAttribute = (node: Element, data: UponSanitizeAttributeHookEvent) => {
    if (data.attrName === 'style') {
      const sanitizedValue = sanitizeCssDeclarationBlock(data.attrValue)
      data.attrValue = sanitizedValue
      if (!sanitizedValue) data.keepAttr = false
      return
    }
    if (data.attrName === 'href' && node.tagName === 'A' && !EMAIL_SAFE_HREF_PATTERN.test(data.attrValue)) {
      data.keepAttr = false
      return
    }
    if (data.attrName === 'src' && node.tagName === 'IMG' && !EMAIL_SAFE_SRC_PATTERN.test(data.attrValue)) {
      data.keepAttr = false
    }
  }

  // DOMPurify's documented pattern for making links open safely: this hook runs after
  // the attribute allowlist pass for the node has already finished, so target/rel
  // don't need to be added to EMAIL_ALLOWED_ATTR (and any attacker-supplied
  // target/rel was already stripped by the allowlist pass before this ever runs).
  const onAfterSanitizeAttributes = (node: Element) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    }
  }

  purifier.addHook('uponSanitizeElement', onSanitizeElement)
  purifier.addHook('uponSanitizeAttribute', onSanitizeAttribute)
  purifier.addHook('afterSanitizeAttributes', onAfterSanitizeAttributes)

  try {
    return purifier.sanitize(html, {
      ALLOWED_TAGS: EMAIL_ALLOWED_TAGS,
      ALLOWED_ATTR: EMAIL_ALLOWED_ATTR,
      ALLOWED_URI_REGEXP: EMAIL_SAFE_URI_PATTERN,
      ADD_URI_SAFE_ATTR: EMAIL_URI_SAFE_ATTR,
      ALLOW_DATA_ATTR: false,
      FORBID_TAGS: EMAIL_FORBID_TAGS,
      // DOMPurify parses fragments as a full document and only returns doc.body's
      // children. Without this, a <style> tag with no preceding non-whitespace,
      // non-head content (e.g. one that's first in the email body) parses straight
      // into <head> and is silently dropped, since only <body> is ever inspected.
      // FORCE_BODY prepends a synthetic non-head element so parsing always lands
      // in <body>.
      FORCE_BODY: true,
    })
  } finally {
    purifier.removeHook('uponSanitizeElement', onSanitizeElement)
    purifier.removeHook('uponSanitizeAttribute', onSanitizeAttribute)
    purifier.removeHook('afterSanitizeAttributes', onAfterSanitizeAttributes)
  }
}
